import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle, 
  Eye, 
  EyeOff,
  Loader2,
  Save,
  TestTube,
  Activity
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { useToast } from '../lib/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';

export default function RentlyConfigPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMaster = user?.role === 'master';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  
  const [lessors, setLessors] = useState([]);
  const [selectedLessorId, setSelectedLessorId] = useState('');
  const [credentials, setCredentials] = useState(null);
  const [status, setStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    clientId: '',
    clientSecret: '',
    apiBaseUrl: 'https://easycarros.rently.com.ar',
    vehicleStatus: 'active',
    autoSync: true,
    syncFrequency: 24
  });

  const fetchLessors = useCallback(async () => {
    try {
      // Se for master, busca todas as locadoras
      // Se for locadora, não precisa buscar (já tem no user.companyId)
      if (isMaster) {
        const response = await api.get('/locadoras');
        setLessors(response.data);
      } else if (user?.companyId) {
        // Para usuários de locadora, usa apenas a empresa dele
        setLessors([{
          _id: user.companyId._id,
          nomeFantasia: user.companyId.nomeFantasia
        }]);
        setSelectedLessorId(user.companyId._id);
      }
      setLoading(false);
    } catch (error) {
      toast({
        title: t('rently.error.loadLessors'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
      setLoading(false);
    }
  }, [toast, isMaster, user]);

  const fetchCredentials = useCallback(async () => {
    try {
      const response = await api.get(`/rently/credentials/${selectedLessorId}`);
      setCredentials(response.data);
      setFormData({
        clientId: response.data.clientId || '',
        clientSecret: '', // Não retorna por segurança
        apiBaseUrl: response.data.apiBaseUrl || 'https://easycarros.rently.com.ar',
        vehicleStatus: response.data.syncConfig?.vehicleStatus || 'active',
        autoSync: response.data.syncConfig?.autoSync !== false,
        syncFrequency: response.data.syncConfig?.syncFrequency || 24
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        toast({
          title: t('rently.error.loadCredentials'),
          description: error.response?.data?.error || error.message,
          variant: 'destructive'
        });
      }
      setCredentials(null);
    }
  }, [selectedLessorId, toast]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get(`/rently/status/${selectedLessorId}`);
      setStatus(response.data);
    } catch (error) {
      setStatus(null);
    }
  }, [selectedLessorId]);

  // Buscar locadoras
  useEffect(() => {
    fetchLessors();
  }, [fetchLessors]);

  // Buscar credenciais quando selecionar locadora
  useEffect(() => {
    if (selectedLessorId) {
      fetchCredentials();
      fetchStatus();
    }
  }, [selectedLessorId, fetchCredentials, fetchStatus]);

  const handleSave = async () => {
    if (!selectedLessorId) {
      toast({
        title: t('rently.validation.selectLessor'),
        variant: 'destructive'
      });
      return;
    }

    if (!formData.clientId || !formData.clientSecret) {
      toast({
        title: t('rently.validation.fillCredentials'),
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/rently/credentials', {
        lessorId: selectedLessorId,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        apiBaseUrl: formData.apiBaseUrl,
        syncConfig: {
          vehicleStatus: formData.vehicleStatus,
          autoSync: formData.autoSync,
          syncFrequency: formData.syncFrequency
        }
      });

      setCredentials(response.data.credentials);
      
      toast({
        title: t('rently.success.configSaved'),
        description: t('rently.success.credentialsConfigured')
      });

      fetchStatus();
    } catch (error) {
      toast({
        title: t('rently.error.saveConfig'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!selectedLessorId || !credentials) {
      toast({
        title: t('rently.validation.saveBeforeTest'),
        variant: 'destructive'
      });
      return;
    }

    setTesting(true);
    try {
      const response = await api.get(`/rently/test/${selectedLessorId}`);
      
      toast({
        title: t('rently.success.connectionSuccess'),
        description: t('rently.success.connectedAs', { userName: response.data.profile.userName })
      });
    } catch (error) {
      toast({
        title: t('rently.error.connection'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    if (!selectedLessorId || !credentials) {
      toast({
        title: t('rently.validation.configBeforeSync'),
        variant: 'destructive'
      });
      return;
    }

    setSyncing(true);
    setSyncProgress({ progress: 0, processed: 0, total: 0, status: 'starting' });
    
    try {
      // Iniciar sincronização (retorna imediatamente com syncId)
      const response = await api.post(`/rently/sync/${selectedLessorId}`);
      const { syncId } = response.data;

      toast({
        title: t('rently.success.syncStarted'),
        description: t('rently.success.syncInProgress')
      });

      // Fazer polling do status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get(`/rently/sync/status/${syncId}`);
          const syncStatus = statusResponse.data;

          // Atualizar progresso visual
          setSyncProgress({
            progress: syncStatus.progress || 0,
            processed: syncStatus.processed || 0,
            total: syncStatus.total || 0,
            created: syncStatus.created || 0,
            updated: syncStatus.updated || 0,
            errors: syncStatus.errors || 0,
            status: syncStatus.status
          });

          // Verificar se terminou
          if (syncStatus.status === 'completed') {
            clearInterval(pollInterval);
            setSyncing(false);
            setSyncProgress(null);

            toast({
              title: t('rently.success.syncCompleted'),
              description: t('rently.success.syncResults', { 
                created: syncStatus.created, 
                updated: syncStatus.updated 
              })
            });

            fetchStatus();
          } else if (syncStatus.status === 'error') {
            clearInterval(pollInterval);
            setSyncing(false);
            setSyncProgress(null);

            toast({
              title: t('rently.error.sync'),
              description: syncStatus.error || t('common.error'),
              variant: 'destructive'
            });
          }
        } catch (error) {
          // Se não encontrar o status, pode ter terminado
          if (error.response?.status === 404) {
            clearInterval(pollInterval);
            setSyncing(false);
            setSyncProgress(null);
            fetchStatus();
          }
        }
      }, 2000); // Poll a cada 2 segundos

      // Timeout de segurança (2 minutos)
      setTimeout(() => {
        clearInterval(pollInterval);
        if (syncing) {
          setSyncing(false);
          setSyncProgress(null);
          toast({
            title: t('rently.warning.syncTimeout'),
            description: t('rently.warning.checkStatusLater'),
            variant: 'default'
          });
          fetchStatus();
        }
      }, 120000); // 2 minutos

    } catch (error) {
      setSyncing(false);
      setSyncProgress(null);
      toast({
        title: t('rently.error.sync'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('rently.page.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('rently.page.subtitle')}
          </p>
        </div>
      </div>

      {/* Seleção de Locadora - Apenas para usuários master */}
      {isMaster && (
        <Card>
          <CardHeader>
            <CardTitle>{t('rently.selectLessor.title')}</CardTitle>
            <CardDescription>
              {t('rently.selectLessor.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="lessor">{t('rently.selectLessor.label')}</Label>
              <Select value={selectedLessorId} onValueChange={setSelectedLessorId}>
                <SelectTrigger id="lessor">
                  <SelectValue placeholder={t('rently.selectLessor.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {lessors.map((lessor) => (
                    <SelectItem key={lessor._id} value={lessor._id}>
                      {lessor.nomeFantasia}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Informação da Locadora - Para usuários não master */}
      {!isMaster && user?.companyId && (
        <Card>
          <CardHeader>
            <CardTitle>{t('rently.yourLessor.title')}</CardTitle>
            <CardDescription>
              {t('rently.yourLessor.description', { companyName: user.companyId.nomeFantasia })}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Status da Sincronização */}
      {selectedLessorId && status && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t('rently.status.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">{t('rently.status.status')}</p>
                <div className="flex items-center gap-2">
                  {status.lastSyncStatus === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : status.lastSyncStatus === 'error' ? (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  ) : null}
                  <Badge variant={
                    status.lastSyncStatus === 'success' ? 'success' : 
                    status.lastSyncStatus === 'error' ? 'destructive' : 
                    'secondary'
                  }>
                    {status.lastSyncStatus === 'success' ? t('rently.status.success') :
                     status.lastSyncStatus === 'error' ? t('rently.status.error') :
                     t('rently.status.pending')}
                  </Badge>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-gray-500">{t('rently.status.lastSync')}</p>
                <p className="text-sm font-medium">
                  {status.lastSync 
                    ? new Date(status.lastSync).toLocaleString('pt-BR')
                    : t('rently.status.never')}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-gray-500">{t('rently.status.syncedVehicles')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {status.lastSyncCount || 0}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-gray-500">{t('rently.status.autoSync')}</p>
                <Badge variant={status.autoSync ? 'success' : 'secondary'}>
                  {status.autoSync ? t('rently.status.active', { frequency: status.syncFrequency }) : t('rently.status.inactive')}
                </Badge>
              </div>
            </div>

            {status.lastSyncError && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('rently.status.lastSyncError')}</AlertTitle>
                <AlertDescription>{status.lastSyncError}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuração de Credenciais */}
      {selectedLessorId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('rently.credentials.title')}
            </CardTitle>
            <CardDescription>
              {t('rently.credentials.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clientId">{t('rently.credentials.clientId')} *</Label>
              <Input
                id="clientId"
                value={formData.clientId}
                onChange={(e) => setFormData({...formData, clientId: e.target.value})}
                placeholder="seminovos"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientSecret">{t('rently.credentials.clientSecret')} *</Label>
              <div className="relative">
                <Input
                  id="clientSecret"
                  type={showSecret ? 'text' : 'password'}
                  value={formData.clientSecret}
                  onChange={(e) => setFormData({...formData, clientSecret: e.target.value})}
                  placeholder={credentials ? '••••••••••••••••' : t('rently.credentials.clientSecretPlaceholder')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                {credentials ? t('rently.credentials.keepCurrentSecret') : t('rently.credentials.requiredFirstTime')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apiBaseUrl">{t('rently.credentials.apiUrl')}</Label>
              <Input
                id="apiBaseUrl"
                value={formData.apiBaseUrl}
                onChange={(e) => setFormData({...formData, apiBaseUrl: e.target.value})}
                placeholder="https://easycarros.rently.com.ar"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configurações de Sincronização */}
      {selectedLessorId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {t('rently.sync.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vehicleStatus">{t('rently.sync.vehicleStatus')}</Label>
              <Select 
                value={formData.vehicleStatus} 
                onValueChange={(value) => setFormData({...formData, vehicleStatus: value})}
              >
                <SelectTrigger id="vehicleStatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('rently.sync.status.activeOnly')}</SelectItem>
                  <SelectItem value="inactive">{t('rently.sync.status.inactiveOnly')}</SelectItem>
                  <SelectItem value="all">{t('rently.sync.status.all')}</SelectItem>
                  <SelectItem value="custom">{t('rently.sync.status.custom')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {t('rently.sync.vehicleStatusDesc')}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="autoSync">{t('rently.sync.autoSync')}</Label>
                <p className="text-xs text-gray-500">
                  {t('rently.sync.autoSyncDesc')}
                </p>
              </div>
              <Switch
                id="autoSync"
                checked={formData.autoSync}
                onCheckedChange={(checked) => setFormData({...formData, autoSync: checked})}
              />
            </div>

            {formData.autoSync && (
              <div className="space-y-2">
                <Label htmlFor="syncFrequency">{t('rently.sync.frequency')}</Label>
                <Input
                  id="syncFrequency"
                  type="number"
                  min="1"
                  value={formData.syncFrequency}
                  onChange={(e) => setFormData({...formData, syncFrequency: parseInt(e.target.value) || 24})}
                />
                <p className="text-xs text-gray-500">
                  {t('rently.sync.frequencyDesc')}
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex gap-2">
            <Button onClick={handleSave} disabled={saving || !selectedLessorId}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('rently.actions.saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('rently.actions.saveConfig')}
                </>
              )}
            </Button>

            {credentials && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleTest} 
                  disabled={testing}
                >
                  {testing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('rently.actions.testing')}
                    </>
                  ) : (
                    <>
                      <TestTube className="mr-2 h-4 w-4" />
                      {t('rently.actions.testConnection')}
                    </>
                  )}
                </Button>

                <Button 
                  variant="secondary" 
                  onClick={handleSync} 
                  disabled={syncing}
                >
                  {syncing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('rently.actions.syncing')}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t('rently.actions.syncNow')}
                    </>
                  )}
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      )}

      {/* Progresso da Sincronização */}
      {syncProgress && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('rently.progress.title')}
            </CardTitle>
            <CardDescription className="text-blue-700">
              {t('rently.progress.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Barra de Progresso */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-blue-900">
                <span>{t('rently.progress.progress')}: {syncProgress.processed}/{syncProgress.total} {t('rently.progress.vehicles')}</span>
                <span className="font-semibold">{Math.round(syncProgress.progress)}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${syncProgress.progress}%` }}
                />
              </div>
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{syncProgress.created || 0}</div>
                <div className="text-xs text-gray-600">{t('rently.progress.created')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{syncProgress.updated || 0}</div>
                <div className="text-xs text-gray-600">{t('rently.progress.updated')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{syncProgress.errors || 0}</div>
                <div className="text-xs text-gray-600">{t('rently.progress.errors')}</div>
              </div>
            </div>

            <p className="text-xs text-center text-blue-700 italic mt-4">
              {t('rently.progress.pleaseWait')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Informações */}
      {selectedLessorId && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('rently.info.title')}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
              <li>{t('rently.info.configCredentials')}</li>
              <li>{t('rently.info.testConnection')}</li>
              <li>{t('rently.info.autoSync')}</li>
              <li>{t('rently.info.manualSync')}</li>
              <li>{t('rently.info.vehicleImport')}</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
